import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import RawTable from "../../components/RawTable";
import InflowComparisonPairContainer from "../../components/charts/inflowComparisonPair/InflowComparisonPairContainer";
import React from "react";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

/* ------ static lists ---------------------------------------------------- */
const PARAMETER_GROUPS = [
  { label: "Total Nitrogen", value: 29 },
  { label: "Total Phosphorus", value: 28 },
  { label: "Total Suspended Solids", value: 30 },
];

const FLOW_OPTIONS = [
  { label: "All Flow", value: 3 },
  { label: "Base Flow", value: 2 },
  { label: "Storm Flow", value: 1 },
];

/* ------ defaults + URL-schema ------------------------------------------ */
const CURRENT_YEAR = new Date().getFullYear();

const YEARS_2015_TO_NOW = Array.from(
  { length: CURRENT_YEAR - 2015 + 1 },
  (_, i) => (2015 + i).toString()
);

const DEFAULTS = {
  parameterid: 29,
  flowcond: 3,
  scaletype: "fixed",
  years: [CURRENT_YEAR - 1, CURRENT_YEAR].map(String), // e.g. ["2023","2024"]
};

const schema = z.object({
  parameterid: z.coerce.number().default(DEFAULTS.parameterid),
  flowcond: z.coerce.number().default(DEFAULTS.flowcond),
  scaletype: z.enum(["fixed", "dynamic"]).default(DEFAULTS.scaletype),
  years: z
    .preprocess(
      (v) => (typeof v === "string" ? v.split(",") : v),
      z.array(z.string())
    )
    .default(DEFAULTS.years),
});

/* ======================================================================= */
export default function WqInflowComparison() {
  const { params, setParams } = useQueryParams(schema);

  const COLUMNS = [
    { title: "Month", field: "month" },
    { title: "Year", field: "year" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    {
      title: "Flow Conditions",
      field: "flow_conditions",
      lookup: Object.fromEntries(
        FLOW_OPTIONS.map((opt) => [opt.value, opt.label])
      ),
    },
  ];

  const FILTERS = [
    {
      type: "select",
      param: "parameterid",
      label: "Parameter Group",
      default: String(params.parameterid),
      options: PARAMETER_GROUPS.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    },
    {
      type: "select",
      param: "flowcond",
      label: "Flow Conditions",
      default: String(params.flowcond),
      options: FLOW_OPTIONS.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    },
  ];

  /* -- helper to sync chip-changes back into the URL -------------------- */
  const handleYearChange = (yrs) => setParams({ ...params, years: yrs });

  const handleScaleChange = (newScale) =>
    setParams({ ...params, scaletype: newScale });

  return (
    <>
      <Helmet title="Reservoir Inflow Comparisons" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Reservoir Inflow Comparisons
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        {/* ---------- About this Page ------------------------------------ */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            Compare <strong>monthly average concentrations</strong> at the two
            inflow monitoring sites — <strong>CC-10 (Cherry Creek)</strong> and
            <strong> CT-2 (Cottonwood Creek)</strong>. Select a parameter group
            (<strong>Total Nitrogen</strong>, <strong>Total Phosphorus</strong>,
            or <strong>TSS</strong>) and use the year chips to view one or many
            calendar years side-by-side.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Filter by <strong>flow condition</strong> and choose a
            <strong> fixed</strong> (shared) or <strong>dynamic</strong>{" "}
            (per-chart) y-axis. The charts, table, and URL update{" "}
            <strong>automatically</strong>, so you can bookmark or share your
            exact view.
          </Typography>
        </Section>

        {/* ---------- Filters ------------------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS} onChange={setParams} />
        </Section>

        {/* ---------- Graphs -------------------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Monthly Concentrations by Year
          </Typography>
          <InflowComparisonPairContainer
            parameterid={params.parameterid}
            years={params.years}
            allYears={YEARS_2015_TO_NOW}
            flowcond={params.flowcond}
            scaletype={params.scaletype}
            onYearsChange={handleYearChange}
            onScaleChange={handleScaleChange}
          />
        </Section>

        {/* ---------- About this Graph ---------------------------------- */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>

          <Typography variant="body1" gutterBottom>
            Each bar represents the <strong>average concentration</strong> for a
            given month and year. Charts are shown <strong>side-by-side</strong>{" "}
            so you can directly compare Cherry Creek (CC-10) and Cottonwood
            Creek (CT-2).
          </Typography>

          <Typography variant="body1" gutterBottom>
            Use the <strong>year chips</strong> below the charts to toggle which
            years are displayed. With <strong>Fixed Scale</strong> selected,
            both charts share an identical y-axis to aid visual comparison; with{" "}
            <strong>Dynamic Scale</strong>, each chart scales independently.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Hover over a bar to see the exact value (in the displayed units).
          </Typography>
        </Section>

        {/* ---------- Raw Data ------------------------------------------ */}
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          <RawTable
            endPoint="wq-inflow-comparison"
            params={{
              parameterid: params.parameterid,
              years: params.years.join(","),
              flowcond: params.flowcond,
            }}
            columns={COLUMNS}
          />
        </Section>
      </main>
    </>
  );
}
